import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

const ArrowManager = forwardRef(
  (
    {
      arrows,
      columns,
      rows,
      deleteArrow,
      changeArrowType,
      containerRef,
      zoomLevel,
      editorMode,
      getArrowClass,
      onArrowHover,
      onArrowLeave,
    },
    ref
  ) => {
    const [cellPositions, setCellPositions] = useState({});
    const [activeArrow, setActiveArrow] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    // ✅ Sécurisation anti-NaN dès le départ
    useEffect(() => {
      if (
        !menuPosition ||
        !Number.isFinite(menuPosition.x) ||
        !Number.isFinite(menuPosition.y)
      ) {
        console.warn("🧩 Correction automatique d'un menuPosition invalide :", menuPosition);
        setMenuPosition({ x: 0, y: 0 });
      }
    }, [menuPosition]);

    // ✅ Calcul des positions précises des cellules avec zoom + scroll
    const calculateCellPositions = useCallback(() => {
      if (!containerRef?.current) return;
      const grid = containerRef.current.querySelector(".grai-grid");
      if (!grid) return;

      const gridRect = grid.getBoundingClientRect();
      const scrollX = containerRef.current.scrollLeft;
      const scrollY = containerRef.current.scrollTop;
      const zoomRatio = zoomLevel / 100;

      const cells = grid.querySelectorAll(".grid-cell");
      const positions = {};

      cells.forEach((cell) => {
        const id = cell.id;
        if (!id || !id.startsWith("cell-")) return;

        const [, rowStr, colStr] = id.split("-");
        const rowId = parseInt(rowStr);
        const colId = parseInt(colStr);
        if (isNaN(rowId) || isNaN(colId)) return;

        const rect = cell.getBoundingClientRect();

        // ✅ Coordonnées ajustées selon le scroll et le zoom
        const adjustedX =
          (rect.left - gridRect.left + rect.width / 2 + scrollX) * zoomRatio;
        const adjustedY =
          (rect.top - gridRect.top + rect.height / 2 + scrollY) * zoomRatio;

        positions[`${rowId}-${colId}`] = { x: adjustedX, y: adjustedY };
      });

      setCellPositions(positions);
    }, [containerRef, zoomLevel]);

    // ✅ Permet un recalcul externe (depuis GRAIEditor)
    useImperativeHandle(ref, () => ({
      calculateCellPositions,
    }));

    // ⚙️ Recalcul automatique à chaque changement
    useEffect(() => {
      calculateCellPositions();
      const timer = setTimeout(calculateCellPositions, 300);
      return () => clearTimeout(timer);
    }, [columns, rows, zoomLevel, calculateCellPositions]);

    // ✅ Recalcule aussi en cas de scroll du conteneur
    useEffect(() => {
  const handleScroll = () => calculateCellPositions();

  if (containerRef.current) {
    containerRef.current.addEventListener("scroll", handleScroll);
  }

  return () => {
    if (containerRef.current) {
      containerRef.current.removeEventListener("scroll", handleScroll);
    }
  };
    }, [calculateCellPositions]);


    // ✅ Création des flèches avec position exacte centre à centre
    const createArrowPath = (arrow) => {
      const start = cellPositions[`${arrow.start.rowId}-${arrow.start.colId}`];
      const end = cellPositions[`${arrow.end.rowId}-${arrow.end.colId}`];
      if (!start || !end) return null;

      return {
        path: `M ${start.x} ${start.y} L ${end.x} ${end.y}`,
        color: arrow.type === "decision" ? "#2563EB" : "#DC2626",
        thickness: arrow.type === "decision" ? 7 : 2,
      };
    };

    // ✅ Gestion du clic sur une flèche
    const handleArrowClick = (e, arrow) => {
      if (editorMode === "delete") {
        deleteArrow(arrow.id);
        return;
      }

      e.stopPropagation();

      const rect = containerRef?.current?.getBoundingClientRect?.() || {
        left: 0,
        top: 0,
      };

      const rawX = Number(e?.clientX) || 0;
      const rawY = Number(e?.clientY) || 0;
      const left = Number(rect?.left) || 0;
      const top = Number(rect?.top) || 0;

      const x = rawX - left;
      const y = rawY - top;

      setMenuPosition({
        x: Number.isFinite(x) ? x : 0,
        y: Number.isFinite(y) ? y : 0,
      });

      setActiveArrow(arrow);
    };

    // ✅ Changement de type ou suppression
    const handleTypeChange = (type) => {
      if (activeArrow) {
        changeArrowType(activeArrow.id, type);
        setActiveArrow(null);
      }
    };

    const handleDelete = () => {
      if (activeArrow) {
        deleteArrow(activeArrow.id);
        setActiveArrow(null);
      }
    };

    // ✅ Valeurs sécurisées
    const safeX =
      menuPosition && Number.isFinite(menuPosition.x) ? menuPosition.x : 0;
    const safeY =
      menuPosition && Number.isFinite(menuPosition.y) ? menuPosition.y : 0;

    return (
      <div className="arrow-container">
        <svg
          className="arrow-svg"
          width={containerRef.current?.scrollWidth || "100%"}
          height={containerRef.current?.scrollHeight || "100%"}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <defs>
            <marker
              id="arrowhead-decision"
              markerWidth="14"
              markerHeight="14"
              refX="12"
              refY="7"
              orient="auto"
            >
              <polygon points="0 0, 14 7, 0 14" fill="#3B82F6" />
            </marker>
            <marker
              id="arrowhead-information"
              markerWidth="10"
              markerHeight="6"
              refX="10"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#EF4444" />
            </marker>
          </defs>

          {/* 🔽 Flèches dessinées ici */}
          {arrows.map((arrow) => {
            const pathData = createArrowPath(arrow);
            if (!pathData) return null;

            const markerId =
              arrow.type === "decision"
                ? "arrowhead-decision"
                : "arrowhead-information";

            return (
              <path
                key={arrow.id}
                id={`arrow-${arrow.id}`}
                d={pathData.path}
                fill="none"
                stroke={pathData.color}
                strokeWidth={pathData.thickness}
                markerEnd={`url(#${markerId})`}
                style={{ pointerEvents: "stroke", cursor: "pointer" }}
                onClick={(e) => handleArrowClick(e, arrow)}
                onMouseEnter={(e) => onArrowHover?.(arrow, e)}
                onMouseLeave={onArrowLeave}
              />
            );
          })}
        </svg>

        {/* ✅ Menu contextuel sécurisé */}
        {activeArrow && (
          <div
            className="arrow-control-panel"
            style={{
              position: "absolute",
              left: `${safeX}px`,
              top: `${safeY}px`,
              zIndex: 1000,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "10px",
            }}
          >
            
            <h4>Type de relation :</h4>
            <button onClick={() => handleTypeChange("information")}>
              Flèche d’information
            </button>
            <button onClick={() => handleTypeChange("decision")}>
              Flèche de décision
            </button>
            <button onClick={handleDelete}>Supprimer</button>
          </div>
        )}
      </div>
      
    );
  }
);

export default ArrowManager;
